import clsx from 'clsx';
import Bubble from 'core/modules/atoms/Bubble';
import { Icon } from 'ui';
import Tooltip from 'core/modules/atoms/Tooltip';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

const HeaderTabs = ({ tabs, tabActive, onChangeTab, onDisableTab, typeView, align, isURLBased }) => {
  const [activeTabState, setActiveTabState] = useState(tabActive);
  const [hasChangedPath, setHasChangedPath] = useState(false);
  const [tabToChangeTo, setTabToChangeTo] = useState(tabs[0]);
  const previousTabActiveRef = useRef(tabActive);

  const history = useHistory();

  useEffect(() => {
    return history.listen(() => {
      setHasChangedPath(true);
    });
  }, [history]);

  function onClickChangeTab(tab) {
    if (isURLBased) {
      setTabToChangeTo(tab);
    } else {
      if (!tab.disabled) {
        setActiveTabState(tab.id);
        // Call onChangeTab immediately for user interactions
        if (onChangeTab) {
          onChangeTab(tab.id);
        }
      } else {
        onDisableTab(tab.id);
      }
    }
    if (tab.onClick) {
      tab.onClick();
    }
  }

  useEffect(() => {
    if (hasChangedPath && isURLBased && tabToChangeTo) {
      setActiveTabState(tabToChangeTo.id);
      setHasChangedPath(false);
    }
  }, [hasChangedPath]);

  useEffect(() => {
    // Only update local state if tabActive changed from parent
    // and is different from current state
    if (tabActive !== previousTabActiveRef.current && tabActive !== activeTabState) {
      setActiveTabState(tabActive);
    }
    // Always update the ref to track the latest tabActive
    previousTabActiveRef.current = tabActive;
  }, [tabActive, activeTabState]);

  function renderText(text, icon, active) {
    return (
      <div className={`header-tabs__tab-text-container `}>
        {icon && (
          <div className="header-tabs__tab-icon">
            <Icon name={icon} color={active ? 'primary' : 'default'} />
          </div>
        )}
        <span className="header-tabs__tab-text">{text}</span>
      </div>
    );
  }

  function renderTab(tab) {
    const className = clsx('header-tabs__tab', `header-tabs__tab--id-${tab.id}`, {
      'header-tabs__tab--active': tab.id === activeTabState,
      'header-tabs__tab--with-icon': tab.icon,
      'header-tabs__tab--disabled': tab.disabled,
    });

    const content = (
      <>
        {tab.bubble && (
          <div className="header-tabs__bubble">
            <Bubble />
          </div>
        )}
        {renderText(tab.name, tab.icon, tab.id === activeTabState)}
      </>
    );

    const tabContent = tab.to ? (
      <Link key={tab.id} to={tab.to} tabIndex={tab.disabled ? -1 : undefined} onClick={() => onClickChangeTab(tab)} className={className}>
        {content}
      </Link>
    ) : (
      <button key={tab.id} type="button" tabIndex={tab.disabled ? -1 : undefined} onClick={() => onClickChangeTab(tab)} className={className}>
        {content}
      </button>
    );

    // Wrap with Tooltip if tooltip property exists
    if (tab.tooltip) {
      return (
        <Tooltip key={tab.id} title={tab.tooltip} placement="right" variant="dark">
          {tabContent}
        </Tooltip>
      );
    }

    return tabContent;
  }

  return <div className={clsx('header-tabs', `header-tabs__type-view-${typeView}`, `header-tabs__align-${align}`)}>{tabs.map((tab) => renderTab(tab))}</div>;
};

HeaderTabs.propTypes = {
  tabs: PropTypes.array, // tabs: [{id: 1, name: "primera Tab", tooltip: "Optional tooltip text"}]
  tabActive: PropTypes.string,
  onChangeTab: PropTypes.func,
  typeView: PropTypes.oneOf(['default', 'border', 'background', 'background-round', 'vertical', 'pill']),
  align: PropTypes.oneOf(['default', 'left', 'right']),
  isURLBased: PropTypes.bool,
  onDisableTab: PropTypes.func,
};

HeaderTabs.defaultProps = {
  tabs: [],
  align: 'default',
  typeView: 'default',
  isURLBased: false,
  onDisableTab: () => {},
};

export default HeaderTabs;
